import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const SECTION = 'cart-drawer-section';

/** Modal presentation around Shopify's native cart lines and section rendering. */
class FivePointCart extends Component {
  requiredRefs = ['panel'];
  #previousFocus;
  #closing;
  #busy = false;
  #nativeUpdates = 0;
  #listeners;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.refs.panel.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
    this.refs.panel.addEventListener('click', event => {
      if (event.target !== this.refs.panel) return;
      const box = this.refs.panel.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.close();
    }, options);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onUpdate, options);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#closing?.cancel();
    unlockScroll(this.refs.panel);
  }

  #setBusy() {
    const busy = this.#busy || this.#nativeUpdates > 0;
    this.setAttribute('aria-busy', String(busy));
    this.querySelectorAll('[data-fp-bag-checkout],[data-fp-bag-clear]').forEach(button => { button.disabled = busy; });
  }

  #error(message) {
    this.querySelector('[data-fp-bag-error-text]').textContent = message;
    this.querySelector('[data-fp-bag-error]').hidden = !message;
  }

  open() {
    const panel = this.refs.panel;
    this.#closing?.cancel();
    this.#closing = null;
    if (panel.open) return;
    this.#previousFocus = document.activeElement;
    panel.showModal();
    lockScroll(panel);
    document.querySelectorAll('[aria-controls="cart-drawer"]').forEach(button => button.setAttribute('aria-expanded', 'true'));
    this.querySelector('.fp-bag__close')?.focus({ preventScroll: true });
  }

  close() {
    const panel = this.refs.panel;
    if (!panel.open || this.#closing) return;
    const finish = () => {
      panel.close();
      unlockScroll(panel);
      document.querySelectorAll('[aria-controls="cart-drawer"]').forEach(button => button.setAttribute('aria-expanded', 'false'));
      if (this.#previousFocus?.isConnected) this.#previousFocus.focus({ preventScroll: true });
      this.#closing = null;
    };
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) { finish(); return; }
    this.#closing = panel.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(100%)' }], { duration: 230, easing: 'ease-in', fill: 'forwards' });
    this.#closing.onfinish = () => { this.#closing.cancel(); finish(); };
  }

  toggle() { if (this.refs.panel.open) this.close(); else this.open(); }

  #onUpdate = event => {
    if (!event.promise) return;
    const sourceDialog = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
    this.#nativeUpdates++;
    this.#setBusy();
    event.promise.then(({ detail }) => {
      this.#error('');
      if (event.action !== 'add' || detail?.didError || !this.hasAttribute('auto-open')) return;
      const open = () => requestAnimationFrame(() => this.open());
      if (sourceDialog?.open && sourceDialog !== this.refs.panel) sourceDialog.addEventListener('close', open, { once: true });
      else open();
    }).catch(() => {
      this.#error('The cart update could not be confirmed. Review your cart before trying again.');
    }).finally(() => { this.#nativeUpdates--; this.#setBusy(); });
  };

  async #request(url, options = {}) {
    const response = await fetch(url, { credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, ...options, signal: AbortSignal.timeout(20000) });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(typeof data.description === 'string' ? data.description : 'The cart update could not be confirmed.');
    return data;
  }

  async #renderAndNotify(cart, sections, action, lines = []) {
    if (typeof sections?.[SECTION] !== 'string' || !sections[SECTION].includes('data-hydration-key="cart-drawer-inner"')) throw new Error('Your cart changed, but the shopping bag could not refresh.');
    if (!Array.isArray(cart.items) || !Number.isFinite(cart.item_count)) throw new Error('The cart response was incomplete.');
    await morphSection(SECTION, sections[SECTION], { mode: 'hydration', injectStylesheet: true });
    const items = this.querySelector('cart-items-component');
    if (!items) throw new Error('The shopping bag could not refresh.');
    // Dispatch from the hydrated native component so it does not render twice.
    items.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: Promise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, sections, source: 'fivepoint-cart', didError: false },
    }) }));
    this.#error('');
  }

  async addItems(items, eventContext = {}) {
    if (this.#busy || this.#nativeUpdates) throw new Error('Please wait for the current cart update.');
    if (!Array.isArray(items) || !items.length || items.length > 40 || items.some(item => !/^\d+$/.test(String(item.id)) || item.quantity !== 1)) throw new Error('The selected colour cards are invalid.');
    this.#busy = true;
    this.#setBusy();
    let result = 'unknown', refresh = 'not_attempted';
    try {
      const added = await this.#request(this.dataset.addUrl, { method: 'POST', body: JSON.stringify({ items, sections: [SECTION], sections_url: location.pathname }) });
      if (!Array.isArray(added.items) || added.items.length !== items.length) throw new Error('The cart response was incomplete.');
      result = 'committed'; refresh = 'failed';
      const cart = await this.#request(`${this.dataset.cartUrl}.js`);
      await this.#renderAndNotify(cart, added.sections, 'add', items.map(item => ({ merchandiseId: item.id, quantity: item.quantity })));
      refresh = 'success';
      this.open();
    } finally {
      window.FivePointEvents?.emit('fp_cart_write_result', {result,refresh_result:refresh,item_count:items.length}, {source_surface:eventContext.source_surface || 'cart',component_id:eventContext.component_id || 'cart-batch-add'});
      this.#busy = false; this.#setBusy();
    }
  }

  async refreshAndOpen() {
    if (this.#busy || this.#nativeUpdates) return;
    this.#busy = true;
    this.#setBusy();
    this.open();
    try {
      const url = new URL(location.href);
      url.searchParams.set('sections', SECTION);
      const [cart, sections] = await Promise.all([this.#request(`${this.dataset.cartUrl}.js`), this.#request(url)]);
      await this.#renderAndNotify(cart, sections, 'update');
    } catch (error) { this.#error(error.message); }
    finally { this.#busy = false; this.#setBusy(); }
  }

  async clearCart() {
    if (this.#busy || this.#nativeUpdates) return;
    this.#busy = true;
    this.#setBusy();
    try {
      const cart = await this.#request(this.dataset.clearUrl, { method: 'POST', body: JSON.stringify({ sections: [SECTION], sections_url: location.pathname }) });
      await this.#renderAndNotify(cart, cart.sections, 'remove');
    } catch (error) { this.#error(`${error.message} Review your cart before trying again.`); }
    finally { this.#busy = false; this.#setBusy(); }
  }
}

if (!customElements.get('fivepoint-cart-component')) customElements.define('fivepoint-cart-component', FivePointCart);
